import { Icon } from '@components/gassan-ui'
import { cn } from '@lib/cn'
import NextLink from 'next/link'
import { AnchorHTMLAttributes, ButtonHTMLAttributes, ForwardedRef, forwardRef } from 'react'

interface ButtonProps {
  iconRight?: 'chevron-right'
  iconLeft?: 'download' | 'chevron-left'
}

export type ButtonType = ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonProps & {
    as: 'button'
    status?: 'idle' | 'disabled'
    disabled?: never
  }

export type ButtonLinkType = AnchorHTMLAttributes<HTMLAnchorElement> &
  ButtonProps & {
    href: string
    status?: never
    as: 'link'
  }

export const SecondaryCta = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonType | ButtonLinkType
>(
  (
    { as: Component = 'button', iconRight, iconLeft, children, className = '', status, ...rest },
    ref,
  ) => {
    const finalClassName = cn(
      'group w-auto flex items-center text-[14px] leading-[1.7] font-bold transition-colors duration-100',
      'text-rolexGreenAlt hover:text-rolexBlack',
      'disabled:pointer-events-none disabled:opacity-50',
      className,
    )

    if (Component === 'link') {
      const props = rest as Omit<ButtonLinkType, 'as'>
      const { href } = props
      return (
        <NextLink ref={ref as ForwardedRef<HTMLAnchorElement>} href={href}>
          <a className={finalClassName} {...props}>
            <InnerButton />
          </a>
        </NextLink>
      )
    }

    return (
      <Component
        ref={ref as ForwardedRef<HTMLButtonElement>}
        className={finalClassName}
        disabled={status === 'disabled'}
        {...(rest as Omit<ButtonType, 'as'>)}
      >
        <InnerButton />
      </Component>
    )

    function InnerButton() {
      return (
        <>
          {iconLeft === 'download' && (
            <svg version="1.1" viewBox="0 0 15 15" height={14} width={14} className="mr-2">
              <path
                d="M15 10v5H0v-5h2v3h11v-3h2zm-9.5-.5l2 2 2-2 2-2h-3V0h-2v7.5h-3l2 2z"
                className="fill-current"
              ></path>
            </svg>
          )}
          {iconLeft === 'chevron-left' && (
            <Icon
              icon="chevron-left"
              size={28}
              className="transition-transform group-hover:-translate-x-1"
            />
          )}
          {children}
          {iconRight === 'chevron-right' && (
            <Icon
              icon="chevron-right"
              size={28}
              className="transition-transform group-hover:translate-x-1"
            />
          )}
        </>
      )
    }
  },
)

SecondaryCta.displayName = 'SecondaryCta'
